import { getApps, initializeApp, type FirebaseApp } from 'firebase/app'
import {
  getAuth,
  initializeAuth,
  inMemoryPersistence,
  signOut,
  type Auth,
  type ConfirmationResult,
} from 'firebase/auth'
import { getFunctions, type Functions } from 'firebase/functions'
import { type AppCheck } from 'firebase/app-check'
import { firebaseConfig, initAppCheckFor } from './firebase'
import { OPS_CALLABLE_REGION } from './functionRegions.generated'
import { sendOutsidePhoneOtp, clearOutsidePhoneVerifier } from './outsidePhoneOtp'

export const BOOKING_APP_NAME = 'ops-booking-auth'

export type { ConfirmationResult }

let bookingApp: FirebaseApp | undefined
let bookingAppCheck: AppCheck | undefined
let bookingAppCheckInitialized = false
let bookingAuth: Auth | undefined
let bookingFunctions: Functions | undefined

export function getBookingApp(): FirebaseApp {
  if (!bookingApp) {
    const existing = getApps().find((a) => a.name === BOOKING_APP_NAME)
    bookingApp = existing ?? initializeApp(firebaseConfig, BOOKING_APP_NAME)
  }
  return bookingApp
}

export function getBookingAppCheck(): AppCheck | undefined {
  if (!bookingAppCheckInitialized) {
    bookingAppCheckInitialized = true
    bookingAppCheck = initAppCheckFor(getBookingApp())
  }
  return bookingAppCheck
}

export function getBookingAuth(): Auth {
  getBookingAppCheck()
  if (!bookingAuth) {
    const app = getBookingApp()
    try {
      bookingAuth = initializeAuth(app, {
        persistence: inMemoryPersistence,
      })
    } catch {
      bookingAuth = getAuth(app)
    }
  }
  return bookingAuth
}

export function getBookingFunctions(): Functions {
  getBookingAppCheck()
  if (!bookingFunctions) {
    bookingFunctions = getFunctions(getBookingApp(), OPS_CALLABLE_REGION)
  }
  return bookingFunctions
}

export function resetBookingAppForTesting(): void {
  clearOutsidePhoneVerifier()
  bookingApp = undefined
  bookingAppCheck = undefined
  bookingAppCheckInitialized = false
  bookingAuth = undefined
  bookingFunctions = undefined
}

export async function sendBookingPhoneOtp(
  phoneNumber: string,
  container?: HTMLElement | string,
): Promise<ConfirmationResult> {
  const auth = getBookingAuth()
  return sendOutsidePhoneOtp(auth, phoneNumber, container, 'booking-recaptcha-container')
}

export async function signOutBookingAuth(): Promise<void> {
  clearOutsidePhoneVerifier()
  if (bookingAuth) {
    try {
      await signOut(bookingAuth)
    } catch {
      // ignore
    }
  }
}
